import React, { useEffect, useMemo, useRef } from 'react';
import { GoogleMap, Polygon, Polyline, useJsApiLoader } from '@react-google-maps/api';
import polyline from 'google-polyline';

const parseSize = (size) => {
  if (!size || typeof size !== 'string' || !size.includes('x')) {
    return { width: 300, height: 200 };
  }
  const [w, h] = size.split('x').map((v) => Number(v));
  return {
    width: Number.isFinite(w) ? w : 300,
    height: Number.isFinite(h) ? h : 200,
  };
};

const parseGoogleColor = (color) => {
  if (!color || typeof color !== 'string') return null;
  const cleaned = color.replace(/^0x/i, '');
  if (cleaned.length !== 8) return null;
  const r = cleaned.slice(0, 2);
  const g = cleaned.slice(2, 4);
  const b = cleaned.slice(4, 6);
  const a = cleaned.slice(6, 8);
  const alpha = parseInt(a, 16) / 255;
  return {
    rgb: `#${r}${g}${b}`,
    opacity: Number.isFinite(alpha) ? alpha : 0.2,
  };
};

const normalizePoints = (points) => {
  if (!points) return [];

  if (typeof points === 'string') {
    if (points.startsWith('enc:')) {
      const encoded = decodeURIComponent(points.slice(4));
      return polyline.decode(encoded).map(([lat, lng]) => ({ lat, lng }));
    }

    if (points.includes('|')) {
      return points
        .split('|')
        .map((pair) => {
          const [latStr, lngStr] = pair.split(',');
          const lat = Number(latStr);
          const lng = Number(lngStr);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return { lat, lng };
        })
        .filter(Boolean);
    }
  }

  if (Array.isArray(points)) {
    return points
      .map((pt) => {
        if (pt && typeof pt === 'object' && Number.isFinite(pt.lat) && Number.isFinite(pt.lng)) {
          return { lat: pt.lat, lng: pt.lng };
        }
        if (Array.isArray(pt) && pt.length >= 2) {
          const a = Number(pt[0]);
          const b = Number(pt[1]);
          if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
          const isLatLng = Math.abs(a) <= 90 && Math.abs(b) <= 180;
          return isLatLng ? { lat: a, lng: b } : { lat: b, lng: a };
        }
        return null;
      })
      .filter(Boolean);
  }

  return [];
};

export const Path = () => null;

export const StaticGoogleMap = ({ size, maptype = 'satellite', apiKey, className, style, children }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-maps-static',
    googleMapsApiKey: apiKey,
  });

  useEffect(() => {
    if (loadError) {
      console.error('Google Maps JS API load error:', loadError);
    }
  }, [loadError]);

  const mapRef = useRef(null);
  const { width, height } = useMemo(() => parseSize(size), [size]);

  const pathProps = useMemo(() => {
    const nodes = React.Children.toArray(children);
    const pathNode = nodes.find((child) => child && child.type === Path);
    return pathNode ? pathNode.props : null;
  }, [children]);

  const points = useMemo(() => normalizePoints(pathProps?.points), [pathProps?.points]);
  const defaultFill = { rgb: '#ffff00', opacity: 0.2 };
  const fill = parseGoogleColor(pathProps?.fillcolor) || (!pathProps?.color ? defaultFill : null);

  const strokeColor = pathProps?.color ? pathProps.color.replace(/^0x/i, '#') : (fill ? fill.rgb : '#ffff00');
  const strokeWeight = Number(pathProps?.weight) || 1;

  const mapOptions = useMemo(() => ({
    mapTypeId: maptype,
    disableDefaultUI: true,
    clickableIcons: false,
    keyboardShortcuts: false,
    zoomControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: 'none',
    draggable: false,
  }), [maptype]);

  const onLoad = (map) => {
    mapRef.current = map;
    if (points.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((pt) => bounds.extend(pt));
      map.fitBounds(bounds, { top: 8, right: 8, bottom: 8, left: 8 });
      window.google.maps.event.addListenerOnce(map, 'idle', () => {
        const currentZoom = map.getZoom();
        if (!Number.isFinite(currentZoom)) return;
        const minZoom = 12;
        const maxZoom = 18;
        const clampedZoom = Math.min(maxZoom, Math.max(minZoom, currentZoom));
        if (clampedZoom !== currentZoom) {
          map.setZoom(clampedZoom);
        }
      });
    }
  };

  if (!isLoaded) {
    return (
      <div
        className={className}
        style={{ width, height, background: '#f0f0f0', borderRadius: 4, ...style }}
      />
    );
  }

  return (
    <div className={className} style={{ width, height, ...style }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%', borderRadius: 4 }}
        options={mapOptions}
        center={points[0] || { lat: 0, lng: 0 }}
        zoom={15}
        onLoad={onLoad}
      >
        {points.length > 0 && fill ? (
          <Polygon
            path={points}
            options={{
              strokeColor,
              strokeOpacity: 1,
              strokeWeight,
              fillColor: fill.rgb,
              fillOpacity: fill.opacity,
            }}
          />
        ) : points.length > 0 ? (
          <Polyline
            path={points}
            options={{
              strokeColor,
              strokeOpacity: 1,
              strokeWeight,
            }}
          />
        ) : null}
      </GoogleMap>
    </div>
  );
};

export default StaticGoogleMap;
